import axios from 'axios';
import authService from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Function to get CSRF token from cookies
const getCsrfToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-CSRFToken': getCsrfToken(),
  },
});

export const chatService = {
  getChatSessions: async () => {
    try {
      const response = await api.get('/api/chat/sessions/');
      return response.data;
    } catch (error) {
      console.error('Error getting chat sessions:', error);
      throw error;
    }
  },

  createChatSession: async (title = 'New Chat') => {
    try {
      const response = await api.post('/api/chat/sessions/create/', { title });
      return response.data;
    } catch (error) {
      console.error('Error creating chat session:', error);
      throw error;
    }
  },

  getChatSession: async (sessionId) => {
    try {
      const response = await api.get(`/api/chat/sessions/${sessionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error getting chat session:', error);
      throw error;
    }
  },

  deleteChatSession: async (sessionId) => {
    try {
      await api.delete(`/api/chat/sessions/${sessionId}/`);
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  },

  getChatHistory: async () => {
    try {
      const response = await api.get('/api/chat/history/');
      return response.data;
    } catch (error) {
      console.error('Error getting chat history:', error);
      throw error;
    }
  },

  sendMessage: async (message, file = null) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('prompt', message);
      
      // Determine file field name based on message content
      if (file) {
        const messageLower = message.toLowerCase();
        if (messageLower.includes('architecture') || messageLower.includes('neural')) {
          formData.append('pkl_file', file);
        } else if (messageLower.includes('nodes')) {
          formData.append('nw', file);
        } else if (messageLower.includes('phishing')) {
          formData.append('emails', file);
        } else {
          // Default file field
          formData.append('file', file);
        }
      }
      
      // Use FormData if we have a file, otherwise use JSON
      const config = file ? {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': getCsrfToken(),
        },
        withCredentials: true
      } : {
        withCredentials: true
      };
      
      const response = await api.post(
        `${API_BASE_URL}/discovery`,
        file ? formData : { prompt: message },
        config
      );
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },
};

export const contactService = {
  submitForm: async (formData) => {
    try {
      // Transform the form data to match backend expectations
      const transformedData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.workEmail,
        company: formData.company,
        message: formData.requirements,
      };

      const response = await api.post('/api/contact/', transformedData);
      return response.data;
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    }
  },
};

// Add request interceptor to handle both auth and CSRF tokens
api.interceptors.request.use(
  (config) => {
    // Add authentication token if user is logged in
    const user = authService.getCurrentUser();
    if (user && user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }

    // Add CSRF token from cookie
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await authService.refreshToken();
        const user = authService.getCurrentUser();
        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${user.token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, logout the user
        authService.logout();
        window.location.href = '/api/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
